"use client"

import { useEffect, useState } from "react"

import type { AndroidBrand, DeviceOs } from "../types"

export type DetectedDevice = {
  /** null in SSR / when UA is unrecognised (treat as desktop/iOS-default). */
  os: DeviceOs | null
  /** Android only; null on iOS and when the brand can't be inferred. */
  brand: AndroidBrand | null
  /** Major iOS version, e.g. 17 for "OS 17_4". Null off-iOS. */
  iosVersion: number | null
  /**
   * True when this looks like a desktop/laptop browser (no touch + not iOS
   * /Android UA). Used to decide whether to surface "send to phone" QRs.
   */
  isDesktop: boolean
  /**
   * False during the first client render (SSR placeholder). Flips to true
   * after the UA sniff has actually run, so callers can wait before
   * applying defaults that would otherwise cause a flash of wrong content.
   */
  detected: boolean
}

const ANDROID_BRAND_PATTERNS: Array<{ brand: AndroidBrand; re: RegExp }> = [
  { brand: "samsung", re: /samsung|SM-|GT-|Galaxy/i },
  // Pixel and the older Nexus line both use stock Android's Settings paths.
  { brand: "pixel", re: /Pixel|Nexus/i },
  // Xiaomi/Redmi/POCO UAs intentionally fall through to "generic" — we
  // don't surface a dedicated Xiaomi pill in BrandPills anymore (2026-05-18).
  // Step data for android-xiaomi is still in data/steps.ts so deep links
  // with ?device=android-xiaomi keep working, but no UA auto-routes there.
]

function classifyAndroidBrand(ua: string): AndroidBrand {
  for (const { brand, re } of ANDROID_BRAND_PATTERNS) {
    if (re.test(ua)) return brand
  }
  return "generic"
}

function parseIosVersion(ua: string): number | null {
  // UA fragment looks like "CPU iPhone OS 17_4_1 like Mac OS X" or
  // "CPU OS 18_0". Major version is the first capture group.
  const match = /CPU (?:iPhone )?OS (\d+)(?:_\d+)*/i.exec(ua)
  return match ? Number(match[1]) : null
}

/**
 * Detects what device/OS the user is on from `navigator.userAgent`. SSR-safe:
 * the first render returns a `detected: false` placeholder so callers won't
 * cause hydration mismatches by pre-selecting tabs based on UA.
 *
 * UA sniffing is approximate by design. iPad >= 13 reports as Mac (Safari's
 * "request desktop site" default) — we accept that as "desktop", because for
 * eSIM install purposes iPad-as-Mac users will still need to send the guide
 * to a phone anyway.
 */
export function useDeviceDetect(): DetectedDevice {
  const [state, setState] = useState<DetectedDevice>({
    os: null,
    brand: null,
    iosVersion: null,
    isDesktop: false,
    detected: false,
  })

  useEffect(() => {
    if (typeof navigator === "undefined") return

    const ua = navigator.userAgent || ""
    const isIos = /iPhone|iPad|iPod/i.test(ua)
    const isAndroid = /Android/i.test(ua)

    if (isIos) {
      setState({
        os: "ios",
        brand: null,
        iosVersion: parseIosVersion(ua),
        isDesktop: false,
        detected: true,
      })
      return
    }

    if (isAndroid) {
      setState({
        os: "android",
        brand: classifyAndroidBrand(ua),
        iosVersion: null,
        isDesktop: false,
        detected: true,
      })
      return
    }

    setState({
      os: null,
      brand: null,
      iosVersion: null,
      isDesktop: true,
      detected: true,
    })
  }, [])

  return state
}

/** iOS 17.4+ added the one-tap eSIM installation link. */
export function supportsIosDeeplink(detected: DetectedDevice): boolean {
  if (detected.os !== "ios" || detected.iosVersion == null) return false
  // Major version >= 18 always qualifies; 17 needs us to know the minor too,
  // which we don't bother parsing — bias towards showing the deeplink (worst
  // case: it falls back to the QR on older minors, no harm done).
  return detected.iosVersion >= 17
}
